import React, { useContext, useState, useCallback } from 'react';
import proptypes from 'prop-types';
import {
  params,
  extend,
  string as stringType,
  number as numberType,
  arrayOf as arrayType,
  shape as shapeType,
  customType,
  useDataModel
} from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import {
  Button, TextField
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ProductListContext } from '../ProductListContext';
import {
  cleanIntent
} from '../productListHelper';

const { string, bool } = proptypes;

export const IntentInput = ({ groupId, disabled = false }) => {
  const [intentText, setIntentText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const {
    list, refetch, onEditSuccess
  } = useContext(ProductListContext);

  const [listOperations] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    lazy: true
  });

  const handleInputChange = useCallback((event) => {
    setIntentText(event.target.value);
  }, []);

  const handleAddIntent = useCallback(async () => {
    if (!intentText.trim() || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Find the current group to determine the next itemOrder
      const currentGroup = list?.groups?.find((group) => group.groupId === actualGroupId);
      const nextItemOrder = (currentGroup?.items?.length || 0) + 1;

      // Use AddItems operation to add new intent to the group
      await listOperations({
        variables: {
          operationName: 'AddItems',
          id: list?.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intent: cleanIntent(intentText),
              quantity: 1,
              itemOrder: nextItemOrder
            }]
          }]
        }
      });

      // Only clear input and trigger success callback after successful API call
      await refetch();
      setIntentText('');
      onEditSuccess({});

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding intent:', error);
    } finally {
      setIsLoading(false);
    }
  }, [intentText, isLoading, groupId, list, listOperations, refetch, onEditSuccess]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAddIntent();
    }
  }, [handleAddIntent]);

  // Apply the exact same responsive classes as IntentPod
  const gridContainerClasses = classNames('sui-w-full sui-grid sui-grid-cols-12', {
    'sui-gap-6': !isMobile,
    'sui-gap-2': isMobile
  });

  const textFieldContainerClasses = classNames('sui-grid sui-pt-2px', {
    'sui-col-span-8': !isMobile,
    'sui-col-span-9': isMobile
  });

  const quantityContainerClasses = classNames('sui-pt-2', {
    'sui-col-span-4': !isMobile,
    'sui-col-span-3 sui-mr-2': isMobile
  });

  return (
    <div
      className="sui-flex sui-w-full sui-flex-col sui-overflow-hidden
        sui-border-b-1 sui-mb-4 sui-border-solid sui-border-primary"
    >
      <div className="sui-py-6 sui-px-6 sui-ml-7">
        <div className={gridContainerClasses}>
          {/* Intent Text Field and Add Button - matches IntentPod structure exactly */}
          <div className={textFieldContainerClasses}>
            <div className="sui-flex sui-gap-4">
              <TextField
                fullWidth
                value={intentText}
                placeholder="Describe item needed..."
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={disabled || isLoading}
                InputProps={{
                  inputProps: {
                    'data-testid': 'intent-input-field'
                  }
                }}
              />
              {!isMobile && (
                <div className="sui-pt-2">
                  <Button
                    onClick={handleAddIntent}
                    disabled={disabled || isLoading || !intentText.trim()}
                    aria-label="Add intent"
                    data-testid="intent-add-button"
                    variant="primary"
                  >
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Empty space to match IntentPod quantity area */}
          <div className={quantityContainerClasses}>
            {/* Empty to maintain layout consistency */}
            {isMobile && (
              <div className="sui-pt-2px">
                <Button
                  onClick={handleAddIntent}
                  disabled={disabled || isLoading || !intentText.trim()}
                  aria-label="Add intent"
                  data-testid="intent-add-button"
                  variant="tertiary"
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

IntentInput.propTypes = {
  groupId: string,
  disabled: bool
};

IntentInput.defaultProps = {
  groupId: null,
  disabled: false
};

IntentInput.displayName = 'IntentInput';

// Define dataModel for mockRequestResponse compatibility
IntentInput.dataModel = extend({
  listOperations: params({
    id: stringType().isRequired(),
    operationName: customType('Operation').enum(
      ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'AddItems'),
    groups: arrayType(customType('GroupInput').shape({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: stringType(),
      products: arrayType(customType('ProductInput').shape({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        intent: stringType()
      }))
    }))
  }).mutation().shape({
    id: stringType(),
    groups: arrayType(shapeType({
      groupId: stringType(),
      status: stringType(),
      items: arrayType(shapeType({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        status: stringType(),
        intent: stringType(),
        intentId: stringType()
      }))
    }))
  })
});
